<script lang="ts">
  import { onMount } from 'svelte'
  import { AlertCircle, MapPin, Minus, Plus } from 'lucide-svelte'
  import * as Alert from '$lib/components/ui/alert'
  import Input from '$lib/components/ui/input/input.svelte'
  import InputGooglePlaces from '$components/InputGooglePlaces.svelte'
  import { t } from '$lib/translations'
  import { getInOutFromGooglePlaces } from '$lib/helpers'
  import Button from '$lib/components/ui/button/button.svelte'
  import { areInSameIsland } from '$lib/boundaries'
  import { showLoading } from '$lib/stores/layout'
  import { loaderOptions } from '$lib/google'
  import { Loader } from '@googlemaps/js-api-loader'
  import type { CustomPlace } from '$lib/types'
  import * as Popover from '$lib/components/ui/popover'

  let placesService: google.maps.places.PlacesService

  let startPlace: CustomPlace | undefined
  let endPlace: CustomPlace | undefined
  let errorMessage: string | undefined = undefined
  let inOut: string = ''
  let startLatitude: string = ''
  let startLongitude: string = ''
  let endLatitude: string = ''
  let endLongitude: string = ''
  let departureDate: string = ''
  let arrivalDate: string = ''
  const current = new Date()

  function updatePlaceData(place: CustomPlace, isStartPlace: boolean) {
    const { coordinates } = place

    if (isStartPlace) {
      startPlace = place
      startLatitude = coordinates?.lat?.toString() as string
      startLongitude = coordinates?.lng?.toString() as string
    } else {
      endPlace = place
      endLatitude = coordinates?.lat?.toString() as string
      endLongitude = coordinates?.lng?.toString() as string
    }

    if (endPlace && startPlace) {
      inOut = getInOutFromGooglePlaces(startPlace, endPlace)
    }

    checkIslandConstraint()
  }

  function checkIslandConstraint() {
    if (
      startPlace &&
      endPlace &&
      !areInSameIsland(parseFloat(startLatitude), parseFloat(startLongitude), parseFloat(endLatitude), parseFloat(endLongitude))
    ) {
      errorMessage = t.get('errors.must_be_in_same_island')
    } else {
      errorMessage = ''
    }
  }

  function saveFormData() {
    showLoading.set(true)
    const formData = {
      startPlaceId: startPlace ? startPlace.googleId : null,
      endPlaceId: endPlace ? endPlace.googleId : null,
      inOut,
      startLatitude,
      startLongitude,
      endLatitude,
      endLongitude,
      departureDate,
      arrivalDate,
      paxAdults: paxValues.paxAdults,
      paxChildren: paxValues.paxChildren,
      paxInfants: paxValues.paxInfants
    }
    sessionStorage.setItem('homeFormData', JSON.stringify(formData))
  }
  $: isSubmitDisabled = !!errorMessage
  function restoreFormData() {
    const formDataString = sessionStorage.getItem('homeFormData')
    if (formDataString) {
      const data = JSON.parse(formDataString)
      if (data.startPlaceId) {
        getPlaceDetails(data.startPlaceId, true)
      }
      if (data.endPlaceId) {
        getPlaceDetails(data.endPlaceId, false)
      }
      startLatitude = startLatitude
      startLongitude = startLongitude
      endLatitude = endLatitude
      endLongitude = endLongitude
      departureDate = data.departureDate
      arrivalDate = data.arrivalDate
      paxValues.paxAdults =data.paxAdults
      paxValues.paxChildren =data.paxChildren
      paxValues.paxInfants =data.paxInfants
    }
  }
  async function fetchPlaceFromServer(googleId: string): Promise<any | null> {
    try {
      const response = await fetch(`/api/places?googleId=${googleId}`)
      if (!response.ok) return null
      return await response.json()
    } catch (error) {
      console.error('Error fetching place from server:', error)
      return null
    }
  }
  async function savePlaceToServer(place: CustomPlace): Promise<void> {
    try {
      await fetch('/api/places', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(place)
      })
    } catch (error) {
      console.error('Error saving place to server:', error)
    }
  }
  async function getPlaceDetails(placeId: string, isStartPlace: boolean): Promise<void> {
    if (!placesService) {
      console.error('PlacesService is not initialized.')
      return
    }
    try {
      const serverResult = await fetchPlaceFromServer(placeId)
      if (serverResult) {
        updatePlaceData(serverResult, isStartPlace)
      } else {
        const result = await new Promise<google.maps.places.PlaceResult>((resolve, reject) => {
          placesService.getDetails({ placeId }, (result, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && result) {
              resolve(result)
            } else {
              const errorMessage = `[BookingTab] Error obtaining place details: ${status}`
              console.error(errorMessage)
              reject(errorMessage)
            }
          })
        })

        const customPlace: CustomPlace = {
          googleId: result.place_id ? result.place_id : '',
          name: result.name ? result.name : '',
          coordinates: {
            lat: result.geometry?.location?.lat() ?? 0,
            lng: result.geometry?.location?.lng() ?? 0
          }
        }
        await savePlaceToServer(customPlace)
        updatePlaceData(customPlace, isStartPlace)
      }
    } catch (error) {
      console.error(error)
    }
  }
  const paxValues = {
    paxAdults: 1,
    paxChildren: 0,
    paxInfants: 0,
  }

  function handlePaxesValue(key: string, value: number) {
    paxValues[key as keyof typeof paxValues] += value
  }
  let sessionToken: google.maps.places.AutocompleteSessionToken
  onMount(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loader = new Loader(loaderOptions as any)
    loader.load().then(() => {
      placesService = new google.maps.places.PlacesService(document.createElement('div'))
      restoreFormData()
      sessionToken = new google.maps.places.AutocompleteSessionToken()
    })
  })
</script>

<form action="?/search_services" method="POST" id="booking-tab-form" on:submit={() => saveFormData()}>
  <input type="text" name="in_out" bind:value={inOut} hidden />
  <input type="text" name="start_latitude" bind:value={startLatitude} hidden />
  <input type="text" name="start_longitude" bind:value={startLongitude} hidden />
  <input type="text" name="end_latitude" bind:value={endLatitude} hidden />
  <input type="text" name="end_longitude" bind:value={endLongitude} hidden />

  <div class="form-group relative">
    <label class="text-xs flex uppercase" for="start_place"><MapPin class="h-4 w-4 mr-2"/>{$t('common.where_do_we_pick_you_up')}*</label>
    <InputGooglePlaces
      id="start_place"
      name="start_place"
      bind:value={startPlace}
      placeholder={$t('common.airport') + ', '  +  $t('common.hotel') + ', ' + $t('contact.address') + '...'}
      {sessionToken}
      on:placeSelected={(e) => updatePlaceData(e.detail, true)}
      class="w-full border-none rounded-none focus-visible:ring-transparent text-sm"
      required
    />
  </div>

  <div class="form-group relative">
    <label class="text-xs flex uppercase" for="end_place"><MapPin class="h-4 w-4 mr-2"/>{$t('common.where_do_we_take_you')}*</label>
    <InputGooglePlaces
      id="end_place"
      name="end_place"
      bind:value={endPlace}
      placeholder={$t('common.airport') + ', '  +  $t('common.hotel') + ', ' + $t('contact.address') + '...'}
      {sessionToken}
      on:placeSelected={(e) => updatePlaceData(e.detail, false)}
      class="w-ful border-none rounded-none focus-visible:ring-transparent text-sm"
      required
    />
  </div>
  <div class="form-group">
    <label class="text-xs uppercase" for="departure_date">{$t('common.pickup_date')}*</label>
    <Input
      name="departure_date"
      type="date"
      min={current ? current.toISOString().split('T')[0] : ''}
      bind:value={departureDate}
      class="w-full border-none rounded-none focus-visible:ring-transparent text-sm"
      required
    />
  </div>
  <div class="form-group">
    <label class="text-xs uppercase" for="arrival_date">{$t('common.return_date')}</label>
    <Input
      name="arrival_date"
      min={current ? current.toISOString().split('T')[0] : ''}
      bind:value={arrivalDate}
      type="date"
      class="w-full border-none rounded-none focus-visible:ring-transparent text-sm"
    />
  </div>
  <div class="form-group">
    <label class="text-xs uppercase" for="pax">{$t('common.passengers')}*</label>
    <div class="h-10 mt-1">

    <Popover.Root>
      <Popover.Trigger
        class="flex h-10 w-full items-center justify-between rounded-md  bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50  focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:outline-0"
      >
        {#each Object.entries(paxValues) as [key, value]}
          {` ${value}`} {$t(`common.${key}`)}
        {/each}
      </Popover.Trigger>
      <Popover.Content class="flex flex-col gap-2">
        {#each Object.entries(paxValues) as [key, value]}
          <div class="w-full flex flex-row justify-between">
            <span>{$t(`common.${key}`)}</span>
            <div class="flex flex-row justify-center gap-2">
              <Button
                variant="outline"
                class="rounded-full h-8 w-8"
                size="icon"
                disabled={key === 'paxAdults' ? value <= 1 : value <= 0}
                on:click={() => handlePaxesValue(key, -1)}
              >
                <Minus class="h-4 w-4" />
              </Button>
              <Button variant="outline" class="rounded-full h-8 w-8">{value}</Button>
              <Button variant="outline" class="rounded-full h-8 w-8" size="icon" on:click={() => handlePaxesValue(key, 1)}>
                <Plus class="h-4 w-4" />
              </Button>
            </div>
          </div>
        {/each}
      </Popover.Content>
    </Popover.Root>
  </div>

    <Input
      name="pax_infants"
      type="number"
      bind:value={paxValues.paxInfants}
      min="0"
      class="w-full hidden border-none rounded-none focus-visible:ring-transparent text-sm"
      required
    />
    <Input
      name="pax_children"
      type="number"
      bind:value={paxValues.paxChildren}
      min="0"
      class="w-full hidden border-none rounded-none focus-visible:ring-transparent text-sm"
      required
    />
    <Input
      name="pax_adults"
      type="number"
      bind:value={paxValues.paxAdults}
      min="1"
      class="w-full hidden border-none rounded-none focus-visible:ring-transparent text-sm"
      required
    />
  </div>

  <Button
    type="submit"
    class="rounded-none transition-all hover:bg-cs-light-orange h-12 flex bg-cs-orange  h-12 flex uppercase p-0 text-white ml-4 mt-2 mb-4"
    disabled={isSubmitDisabled}
  >
    <span class="px-8">{$t('common.search')}</span>

  </Button>
</form>

{#if errorMessage}
  <div class="w-full flex items-center justify-center">
    <Alert.Root variant="destructive" class="relative lg:absolute lg:top-[30vh] bg-white mt-5 rounded-none max-w-[90%]">
      <AlertCircle class="h-4 w-4" />
      <Alert.Title>Error</Alert.Title>
      <Alert.Description>{errorMessage}</Alert.Description>
    </Alert.Root>
  </div>
{/if}
<style>
@media only screen and (max-width: 600px) {
  form *{
    height:auto !important;
  }
}

</style>